import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Send as SendIcon,
  MarkChatRead as MarkChatReadIcon,
  MessageOutlined as MessageOutlinedIcon,
  WhatsApp as WhatsAppIcon,
  Mic as MicIcon,
  AttachFile as AttachFileIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  AutoAwesome as AutoAwesomeIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const channelIcons = {
  web: <MessageOutlinedIcon sx={{ fontSize: 13 }} />,
  whatsapp: <WhatsAppIcon sx={{ fontSize: 13 }} />,
  voice: <MicIcon sx={{ fontSize: 13 }} />,
};

/** Purpose: Recognize current and legacy closed states. Why: Resolved threads must share reply-state behavior. */
const isResolved = (status) => ['resolved', 'closed'].includes(String(status || '').trim().toLowerCase());
/** Purpose: Choose a readable conversation status. Why: The header should agree with inbox lifecycle terminology. */
const statusLabel = (status, isBotConversation) => {
  const normalized = String(status || 'open').trim().toLowerCase();
  if (isResolved(normalized)) return 'Resolved';
  if (isBotConversation && normalized === 'open') return 'Active';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/** Purpose: Choose message alignment and styles by sender. Why: Agents, bots, and customers must be distinguishable. */
const bubbleClass = (sender) =>
  sender === 'agent'
    ? 'ml-auto bg-gradient-to-r from-blue-600 to-sky-600 text-white shadow-sm shadow-blue-500/15 rounded-2xl rounded-tr-xs'
    : sender === 'bot'
      ? 'bg-slate-100 dark:bg-slate-800/90 text-slate-900 dark:text-slate-100 border border-slate-200/60 dark:border-white/5 rounded-2xl rounded-tl-xs shadow-2xs'
      : 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-white/10 rounded-2xl rounded-tl-xs shadow-2xs';

/** Purpose: render the selected conversation timeline and reply composer. Why: message history and agent actions belong in one thread-level component. */
export default function InboxThread({
  conversation,
  messages = [],
  loading = false,
  onSendMessage,
  sending = false,
  typing = false,
  typingText = '',
  onMarkRead,
  cannedResponses = [],
  onSendAttachment,
  onAccept,
  onReject,
  onOpenAttachment,
  capabilities = {},
  onTyping,
  onResolve,
  resolving = false,
  showDetails = true,
  onToggleDetails,
  isBotConversation = false,
}) {
  const [draft, setDraft] = useState('');
  const [suggestingAi, setSuggestingAi] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);

  /** Purpose: Search canned responses after the last shortcut marker. Why: Suggestions must match the current composer fragment. */
  const cannedMatches = useMemo(() => {
    const marker = draft.lastIndexOf('#');
    if (marker < 0) return [];
    const query = draft.slice(marker + 1).trim().toLowerCase();
    return cannedResponses.filter((item) => !query || [item.title, item.shortcut, item.content].some((value) => String(value || '').toLowerCase().includes(query))).slice(0, 6);
  }, [cannedResponses, draft]);

  const bottomRef = useRef(null);
  const scrollAreaRef = useRef(null);

  useEffect(() => {
    let interval;
    if (isRecordingAudio) {
      interval = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isRecordingAudio]);

  /** Purpose: Request an AI reply suggestion and populate the draft. Why: The agent retains control over reviewing and sending. */
  const handleSuggestReply = async () => {
    if (!conversation?._id || suggestingAi) return;
    try {
      setSuggestingAi(true);
      const res = await axios.post(`/api/v1/inbox/conversations/${conversation._id}/suggest-reply`);
      if (res.data?.suggestion) {
        setDraft(res.data.suggestion);
      }
    } catch (err) {
      console.error('Suggest reply failed:', err);
    } finally {
      setSuggestingAi(false);
    }
  };

  /** Purpose: Request and display a conversation summary. Why: Agents need a quick way to review long threads. */
  const handleSummarize = async () => {
    if (!conversation?._id || summarizing) return;
    try {
      setSummarizing(true);
      const res = await axios.post(`/api/v1/inbox/conversations/${conversation._id}/summarize`);
      if (res.data?.summary) {
        setAiSummary(res.data.summary);
      }
    } catch (err) {
      console.error('Summarize failed:', err);
    } finally {
      setSummarizing(false);
    }
  };

  /** Purpose: Acquire the microphone and prepare voice-note capture. Why: Audio replies require chunk collection and resource cleanup. */
  const startRecordingAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      /** Purpose: Collect nonempty audio chunks. Why: The attachment must contain all emitted recording data. */
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      /** Purpose: Build the voice-note file, submit it, and release tracks. Why: Finishing capture must deliver the attachment and stop microphone access. */
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        onSendAttachment?.(audioFile);
        stream.getTracks().forEach((track) => track.stop());
        setIsRecordingAudio(false);
        setRecordingDuration(0);
      };

      mediaRecorder.start();
      setIsRecordingAudio(true);
      setRecordingDuration(0);
    } catch (err) {
      console.error('Audio recording failed:', err);
    }
  };

  /** Purpose: Stop an active recorder. Why: Its stop callback finalizes the attachment. */
  const stopRecordingAudio = () => {
    if (mediaRecorderRef.current && isRecordingAudio) {
      mediaRecorderRef.current.stop();
    }
  };

  /** Purpose: Release microphone tracks and reset recording indicators. Why: Leaving capture must not keep the microphone active. */
  const cancelRecordingAudio = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    setIsRecordingAudio(false);
    setRecordingDuration(0);
  };

  useEffect(() => {
    /** Purpose: Scroll the thread to the newest message. Why: Recent replies should remain visible after rendering. */
    const scrollToLatest = () => {
      const scrollArea = scrollAreaRef.current;
      if (scrollArea) scrollArea.scrollTo({ top: scrollArea.scrollHeight, behavior: 'auto' });
    };
    /** Purpose: Repeat scroll alignment after layout settles. Why: Message and attachment heights may change across animation frames. */
    const firstFrame = window.requestAnimationFrame(() => {
      scrollToLatest();
      window.requestAnimationFrame(scrollToLatest);
    });
    const settleTimer = window.setTimeout(scrollToLatest, 80);
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.clearTimeout(settleTimer);
    };
  }, [messages, typing, conversation?._id]);

  /** Purpose: Choose a title from available conversation identity fields. Why: Unnamed contacts still need an identifiable header. */
  const title = useMemo(
    () => conversation?.displayName || conversation?.userId || conversation?.channelUserId || 'Conversation',
    [conversation]
  );

  /** Purpose: Submit a nonblank draft when not busy or recording. Why: Concurrent sends and empty messages must be prevented. */
  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending || isRecordingAudio) return;
    await onSendMessage?.(text);
    setDraft('');
  };

  /** Purpose: Send on Enter while retaining Shift+Enter for new lines. Why: The composer supports quick and multiline replies. */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
      {/* Thread Header */}
      <div className="flex shrink-0 flex-nowrap items-center gap-3 border-b border-slate-200/80 bg-white/95 px-3.5 py-2.5 backdrop-blur-md dark:border-white/10 dark:bg-slate-950/90">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {/* User Initial Avatar */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-xs font-bold text-white shadow-xs ring-1 ring-slate-100 dark:bg-slate-800 dark:ring-white/10">
            {(title || 'U').charAt(0).toUpperCase()}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <h2
                className="truncate text-sm font-bold text-slate-900 dark:text-white tracking-tight"
                title={title}
              >
                {title}
              </h2>

              {conversation?.channel ? (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-slate-200/80 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-300">
                  {channelIcons[conversation.channel]}
                  <span className="capitalize">{conversation.channel}</span>
                </span>
              ) : null}

              {conversation?.status ? (
                <span
                  className={[
                    'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase',
                    isResolved(conversation.status)
                      ? 'border border-slate-200 bg-slate-100 text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-300'
                      : conversation.status === 'open'
                        ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300'
                        : 'border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300',
                  ].join(' ')}
                >
                  {statusLabel(conversation.status, isBotConversation)}
                </span>
              ) : null}
            </div>

            <div className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
              {conversation?.lastMessage || (conversation?.userId ? `ID: ${conversation.userId}` : 'Customer conversation')}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex min-w-0 max-w-[72%] shrink-0 flex-nowrap items-center justify-end gap-1 overflow-x-auto sm:gap-1.5">
          {conversation && (
            <button
              type="button"
              onClick={handleSummarize}
              disabled={summarizing}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-indigo-200/90 bg-indigo-50/80 px-2.5 py-1 text-xs font-semibold text-indigo-700 shadow-2xs transition hover:bg-indigo-100 hover:border-indigo-300 active:scale-95 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200 dark:hover:bg-indigo-500/20 disabled:opacity-50 cursor-pointer"
              title="Generate AI summary of this conversation"
            >
              <AutoAwesomeIcon sx={{ fontSize: 13 }} className={summarizing ? 'animate-spin' : ''} />
              <span className="whitespace-nowrap">AI Summary</span>
            </button>
          )}

          {conversation && !isResolved(conversation.status) ? (
            <button
              type="button"
              onClick={() => onResolve?.()}
              disabled={resolving}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200/90 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 shadow-2xs transition hover:bg-emerald-100 hover:border-emerald-300 active:scale-95 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300 cursor-pointer disabled:cursor-wait disabled:opacity-70"
              title={resolving ? 'Resolving conversation…' : 'Resolve conversation'}
            >
              {resolving ? (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-emerald-600/30 border-t-emerald-700 dark:border-emerald-300/30 dark:border-t-emerald-300" aria-hidden="true" />
              ) : <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
              <span className="whitespace-nowrap">{resolving ? 'Resolving…' : 'Resolve'}</span>
            </button>
          ) : null}

          {conversation && isResolved(conversation.status) ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-300"
              title="This conversation is resolved"
              aria-label="Conversation resolved"
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
              <span className="whitespace-nowrap">Resolved</span>
            </span>
          ) : null}

          <button
            type="button"
            onClick={() => onMarkRead?.()}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200/90 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-2xs transition hover:border-slate-300 hover:bg-slate-50 active:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 cursor-pointer"
            title="Mark as read"
          >
            <MarkChatReadIcon sx={{ fontSize: 13 }} />
              <span className="hidden 2xl:inline whitespace-nowrap">Mark read</span>
          </button>

          {conversation && !isBotConversation && !conversation.assignedTo && !isResolved(conversation.status) ? (
            <div className="flex flex-nowrap items-center gap-1">
              <button
                type="button"
                onClick={onAccept}
                className="shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 text-xs font-semibold text-white shadow-2xs transition active:scale-95 cursor-pointer"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={onReject}
                className="shrink-0 rounded-full border border-rose-200 bg-rose-50 hover:bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-600 shadow-2xs transition active:scale-95 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 cursor-pointer"
              >
                Reject
              </button>
            </div>
          ) : null}

          {onToggleDetails ? (
            <button
              type="button"
              onClick={onToggleDetails}
              className={[
                'hidden xl:inline-flex items-center justify-center h-7 w-7 rounded-full border transition active:scale-95 shadow-2xs cursor-pointer',
                showDetails
                  ? 'border-cyan-300 bg-cyan-50/80 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-slate-400',
              ].join(' ')}
              title={showDetails ? 'Hide details panel' : 'Show details panel'}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </button>
          ) : null}
        </div>
      </div>

      {/* AI Summary Banner */}
      {aiSummary && (
        <div className="mx-4 mt-3 rounded-2xl border border-indigo-200/90 bg-indigo-50/80 p-3 text-xs text-indigo-950 shadow-sm backdrop-blur-sm dark:border-indigo-500/20 dark:bg-indigo-950/40 dark:text-indigo-200">
          <div className="flex items-center justify-between font-bold">
            <span className="flex items-center gap-1.5 text-indigo-700 dark:text-indigo-300">
              <AutoAwesomeIcon sx={{ fontSize: 15 }} />
              AI Conversation Summary
            </span>
            <button type="button" onClick={() => setAiSummary(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white">&times;</button>
          </div>
          <p className="mt-1 leading-relaxed text-slate-700 dark:text-slate-300">{aiSummary}</p>
        </div>
      )}

      {/* Messages Scroll Area */}
      <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/60 px-5 py-4 dark:bg-slate-950/40">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex justify-end">
                <div className="h-16 w-2/3 animate-pulse rounded-2xl bg-slate-200/60 dark:bg-white/10" />
              </div>
            ))}
          </div>
        ) : messages.length ? (
          <div className="mx-auto max-w-4xl space-y-3.5">
            {messages.map((message) => {
              const isAgent = message.sender === 'agent';
              const hasAudio = message.type === 'audio' || message.attachments?.some((a) => a.mimeType?.startsWith('audio/') || a.name?.endsWith('.webm') || a.name?.endsWith('.mp3') || a.name?.endsWith('.wav') || a.name?.endsWith('.ogg'));
              const audioAttachment = message.attachments?.find((a) => a.mimeType?.startsWith('audio/') || a.name?.endsWith('.webm') || a.name?.endsWith('.mp3') || a.name?.endsWith('.wav') || a.name?.endsWith('.ogg'));

              const authToken = typeof window !== 'undefined' ? (localStorage.getItem('token') || '') : '';
              /** Purpose: Build an authenticated conversation attachment URL. Why: Protected media must be requested within its owning thread. */
              const getAttachmentUrl = (attId) =>
                `/api/v1/inbox/conversations/${conversation?._id}/attachments/${attId}${authToken ? `?token=${encodeURIComponent(authToken)}` : ''}`;

              return (
                <div key={message._id || `${message.timestamp}-${message.message}`} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                  <div className={['max-w-[76%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-xs', bubbleClass(message.sender)].join(' ')}>
                    <div className="whitespace-pre-wrap break-words">{message.message || (hasAudio ? 'Voice Note' : '')}</div>
                    {hasAudio ? (
                      <div className="mt-2">
                        <audio
                          controls
                          src={
                            audioAttachment?.attachmentId
                              ? getAttachmentUrl(audioAttachment.attachmentId)
                              : message.mediaUrl || ''
                          }
                          className="h-8 w-56 rounded"
                        />
                      </div>
                    ) : null}
                    {(message.attachments || []).filter((a) => a !== audioAttachment).map((attachment) => {
                      const isImage = attachment.mimeType?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(attachment.name || '');
                      return isImage ? (
                        <div key={attachment.attachmentId} className="mt-2">
                          <img
                            src={getAttachmentUrl(attachment.attachmentId)}
                            alt={attachment.name || 'Image attachment'}
                            className="max-h-60 max-w-full rounded-xl object-contain border border-black/10 dark:border-white/10 cursor-pointer transition hover:opacity-95"
                            onClick={() => onOpenAttachment?.(attachment)}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <button type="button" onClick={() => onOpenAttachment?.(attachment)} className="mt-1 block underline text-[11px] font-semibold opacity-90">{attachment.name}</button>
                        </div>
                      ) : (
                        <button type="button" key={attachment.attachmentId} onClick={() => onOpenAttachment?.(attachment)} className="mt-2 block underline text-xs font-semibold">{attachment.name}</button>
                      );
                    })}
                    <div className={`mt-1.5 flex items-center gap-1.5 text-[11px] font-medium ${isAgent ? 'justify-end text-white/80' : 'text-slate-400 dark:text-slate-500'}`}>
                      {message.senderName ? <span>{message.senderName} ·</span> : message.sender === 'bot' ? <span>Bot ·</span> : null}
                      {message.timestamp ? <span>{format(new Date(message.timestamp), 'HH:mm')}</span> : null}
                    </div>
                  </div>
                </div>
              );
            })}

            {typing ? (
              <div className="flex justify-start">
                <div className="inline-flex items-center gap-2 rounded-2xl border border-sky-200/80 bg-sky-50/80 px-4 py-2 text-xs text-sky-900 shadow-2xs dark:border-sky-500/20 dark:bg-sky-950/40 dark:text-sky-200">
                  <div className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-500 [animation-delay:-0.2s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-500 [animation-delay:-0.1s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-sky-500" />
                  </div>
                  {typingText ? (
                    <span className="font-medium italic">
                      Customer is typing: <span className="font-normal not-italic text-slate-800 dark:text-slate-200">"{typingText}"</span>
                    </span>
                  ) : (
                    <span>Customer is typing...</span>
                  )}
                </div>
              </div>
            ) : null}

            <div ref={bottomRef} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            {conversation ? 'No messages yet. Start the conversation from the input below.' : 'Select a conversation to see the full thread.'}
          </div>
        )}
      </div>

      {/* Composer Input Area */}
      <div className="shrink-0 border-t border-slate-200/80 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950">
        <div className="relative rounded-2xl border border-slate-200 bg-white p-2.5 shadow-xs transition-all focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 dark:border-white/10 dark:bg-slate-900/90">
          {isRecordingAudio ? (
            <div className="flex items-center justify-between py-2 px-1">
              <span className="flex items-center gap-2 text-xs font-semibold text-rose-600 animate-pulse">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-600" />
                Recording Voice Note: {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={stopRecordingAudio}
                  className="rounded-full bg-rose-600 px-3.5 py-1 text-xs font-semibold text-white shadow-xs hover:bg-rose-700"
                >
                  Stop & Send
                </button>
                <button
                  type="button"
                  onClick={cancelRecordingAudio}
                  className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <textarea
              value={draft}
              onChange={(event) => { setDraft(event.target.value); onTyping?.(Boolean(event.target.value)); }}
              onKeyDown={handleKeyDown}
              placeholder="Type a reply... (Press Enter to send, # for canned responses)"
              rows={2}
              className="min-h-[44px] max-h-32 w-full resize-none border-0 bg-transparent px-2 py-1 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0 leading-relaxed dark:text-slate-100"
            />
          )}

          {cannedMatches.length ? (
            <div className="mb-2 max-h-48 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-slate-950">
              {cannedMatches.map((item) => (
                <button key={item._id} type="button" onClick={() => { const marker = draft.lastIndexOf('#'); setDraft(`${draft.slice(0, marker)}${item.content}`); }} className="block w-full rounded-xl px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-white/10">
                  <div className="text-xs font-semibold text-slate-900 dark:text-white">#{item.shortcut} · {item.title}</div>
                  <div className="mt-1 truncate text-xs text-slate-500">{item.content}</div>
                </button>
              ))}
            </div>
          ) : null}

          {/* Action Toolbar */}
          <div className="mt-2 flex items-center justify-between gap-3 pt-1 border-t border-slate-100 dark:border-white/5">
            <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
              {capabilities.file ? (
                <label className="cursor-pointer rounded-full border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-800 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10" title="Attach file">
                  <AttachFileIcon sx={{ fontSize: 16 }} />
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,application/pdf,text/plain,.doc,.docx,audio/mpeg,audio/wav,audio/ogg" onChange={(e) => { const file = e.target.files?.[0]; if (file) onSendAttachment?.(file); e.target.value = ''; }} />
                </label>
              ) : null}

              <button
                type="button"
                onClick={startRecordingAudio}
                className="cursor-pointer rounded-full border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-800 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10"
                title="Record voice note"
              >
                <MicIcon sx={{ fontSize: 16 }} />
              </button>

              <button
                type="button"
                onClick={handleSuggestReply}
                disabled={suggestingAi || !conversation}
                className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 shadow-2xs transition hover:bg-sky-100 active:scale-95 disabled:opacity-40 dark:border-sky-500/20 dark:bg-sky-950/40 dark:text-sky-300"
                title="Ask AI Copilot to suggest a reply"
              >
                <AutoAwesomeIcon sx={{ fontSize: 13 }} className={suggestingAi ? 'animate-spin' : ''} />
                <span>{suggestingAi ? 'Drafting...' : 'Suggest AI Reply'}</span>
              </button>
            </div>

            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !draft.trim() || !capabilities.text || isRecordingAudio}
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              <SendIcon sx={{ fontSize: 14 }} />
              <span>Send</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
